import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useAuth } from '../../store/AuthContext';
import { paths } from '../../routes/paths';
import { resolveOnboardingPath } from '../../lib/onboardingStepPaths';
import { Loader } from '../common/Loader';

/**
 * Gates a route subtree behind authentication + onboarding completion.
 *
 * - Not authenticated → redirect to /sign-in
 * - Authenticated, onboarding incomplete, navigating to non-/onboarding path
 *   → redirect to the current onboarding step
 * - Authenticated, onboarding complete, navigating to /onboarding/*
 *   → redirect to /dashboard
 */
export function ProtectedRoute({ children, requireOnboarding = true }) {
  const { isLoading, isAuthenticated, isOnboardingComplete, onboardingStep } = useAuth();
  const location = useLocation();

  if (isLoading) return <Loader fullscreen />;
  if (!isAuthenticated()) {
    return <Navigate to={paths.signIn} replace state={{ from: location }} />;
  }

  const inOnboarding = location.pathname.startsWith('/onboarding');
  const complete = isOnboardingComplete();

  if (requireOnboarding && !complete && !inOnboarding) {
    return <Navigate to={resolveOnboardingPath(onboardingStep)} replace />;
  }
  if (complete && inOnboarding) {
    return <Navigate to={paths.dashboard} replace />;
  }

  return children || <Outlet />;
}
